import { Button, useKeyboard, UseKeyboardLevel, useOnOutsideClick } from "@hubrise/react-components"
import * as React from "react"
import type { DragDropContextProps } from "react-beautiful-dnd"
import { DragDropContext, Droppable } from "react-beautiful-dnd"

import { moveItemWithinArray } from "shared/utils/javascript"

import Row from "./Row"
import type { ColumnStyle } from "./Styles.Table"
import * as StyledTable from "./Styles.Table"
import { Footer } from "./Styles.Table"

interface Column<ItemValue> {
  header?: string
  style?: ColumnStyle
  cellRenderer: (item: ItemValue, updateForNew?: (item: ItemValue) => void) => React.ReactNode
}

interface AddBehaviorProps<ItemValue> {
  buttonLabel: string
  getNew: () => ItemValue
  tryCreate: (item: ItemValue) => boolean
}

interface TableProps<ItemValue> {
  testId?: string
  items: Array<ItemValue>
  columns: Array<Column<ItemValue>>
  onReorder: (items: Array<ItemValue>) => void
  onDelete?: (item: ItemValue) => void
  droppableId: string
  addBehavior?: AddBehaviorProps<ItemValue>
}

const Table = <ItemValue extends { id: number }>({
  testId,
  items,
  columns,
  onReorder,
  onDelete,
  droppableId,
  addBehavior,
}: TableProps<ItemValue>): JSX.Element => {
  const [adding, setAdding] = React.useState<ItemValue | null>(null)
  const itemsAndAdding: Array<ItemValue> = adding ? [...items, adding] : items
  const $newRowRef = React.useRef<HTMLTableRowElement>(null)

  const isAdding = adding !== null
  useOnOutsideClick([$newRowRef], isAdding, () => setAdding(null))
  useKeyboard(UseKeyboardLevel.Details, { code: "Escape" }, () => setAdding(null), {
    isListening: isAdding,
    description: "cancelAddRow",
  })

  const canDrag = items.length > 1
  const $tableRef = React.useRef<HTMLTableElement>(null)

  // Lock cell widths before drag starts, because the dragged row is taken out of the table layout
  // and its cells would otherwise collapse to their content width.
  const onBeforeDragStart: DragDropContextProps["onBeforeDragStart"] = ({ source }) => {
    const row = $tableRef.current?.querySelectorAll<HTMLElement>("tbody tr")[source.index]
    if (!row) return
    row.querySelectorAll<HTMLElement>("td").forEach((cell) => {
      cell.style.width = `${cell.getBoundingClientRect().width}px`
    })
  }

  const onDragEnd: DragDropContextProps["onDragEnd"] = ({ source, destination }) => {
    $tableRef.current?.querySelectorAll<HTMLElement>("td").forEach((cell) => {
      cell.style.width = ""
    })
    if (destination) {
      const item = items[source.index]
      const reordered = moveItemWithinArray<ItemValue>(items, item, destination.index)
      onReorder(reordered)
    }
  }

  return (
    <>
      <StyledTable.table ref={$tableRef} data-testid={testId}>
        {columns.some((column) => column.header !== undefined) && items.length > 0 && (
          <thead>
            <tr>
              {columns.map((column, index) => (
                <StyledTable.th key={index} $style={column.style}>
                  {column.header}
                </StyledTable.th>
              ))}
              {canDrag && <StyledTable.th />}
            </tr>
          </thead>
        )}

        <DragDropContext onBeforeDragStart={onBeforeDragStart} onDragEnd={onDragEnd}>
          <Droppable droppableId={droppableId}>
            {(provided) => (
              <tbody {...provided.droppableProps} ref={provided.innerRef}>
                {itemsAndAdding.map((item, index) => {
                  const isNew = item === adding
                  const handleDelete = isNew ? (_item: ItemValue) => setAdding(null) : onDelete
                  const updateForNew = isNew
                    ? (item: ItemValue) => (addBehavior!.tryCreate(item) ? setAdding(null) : setAdding(item))
                    : undefined

                  return (
                    <Row
                      ref={isNew ? $newRowRef : undefined}
                      key={item.id}
                      {...{
                        item,
                        index,
                        canDrag,
                        onDelete: handleDelete,
                        cells: columns.map((col) => col.cellRenderer(item, updateForNew)),
                        columnStyles: columns.map((col) => col.style),
                      }}
                    />
                  )
                })}

                {provided.placeholder}
              </tbody>
            )}
          </Droppable>
        </DragDropContext>
      </StyledTable.table>

      {addBehavior && !adding && (
        <Footer>
          <Button icon="add" variant="empty" onClick={() => setAdding(addBehavior.getNew())}>
            {addBehavior.buttonLabel}
          </Button>
        </Footer>
      )}
    </>
  )
}

export default Table

export { Footer }
